import { RightCircleFilled } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { Store } from 'antd/lib/form/interface';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputPassword from 'components/atoms/InputPassword';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import AuthWrapper from 'components/base/AuthWrapper';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import { useNewPassword } from 'hooks/useNewPassword';
import useProfily from 'hooks/useProfily';

import captcha, { ICaptcha } from 'services/captcha';
import { leiMigraceApi } from 'services/lei/leiLogin';
import loginApi from 'services/login';
import registrace from 'services/registrace';

import routePathEnum from 'enums/routePathEnum';

import emailCheckValidation from 'utils/validations/emailCheckValidation';
import emailValidation from 'utils/validations/emailValidation';
import passwordCheckValidation from 'utils/validations/passwordCheckValidation';
import phoneValidation from 'utils/validations/phoneValidation';

interface IRegistrace {}

export const callCaptcha = (
    setLoadingCaptcha: (value: React.SetStateAction<boolean>) => void,
    setCaptchaState: (value: React.SetStateAction<ICaptcha>) => void,
    setErrCaptcha: (value: React.SetStateAction<string | undefined>) => void
) => {
    setLoadingCaptcha(val => !val);
    captcha()
        .then(data => setCaptchaState(data))
        .catch(() => setErrCaptcha('RESPONSE_ERROR_503'))
        .finally(() => setLoadingCaptcha(val => !val));
};

const Registrace: React.FC<IRegistrace> = () => {
    const [form] = useForm();
    const { PasswordInput, isStrongEnough } = useNewPassword('password');
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { tokenURL, language, profily, leiLogin, prevPath } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCaptcha, setLoadingCaptcha] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [errCaptcha, setErrCaptcha] = useState<string>();
    const [renderError, setRenderError] = useState<boolean>(false);
    const [loginDetails, setLoginDetails] = useState<{
        username: string;
        password: string;
    } | null>(null);
    const [captchaState, setCaptchaState] = useState<ICaptcha>({
        id: 0,
        image: ''
    });

    useEffect(() => {
        callCaptcha(setLoadingCaptcha, setCaptchaState, setErrCaptcha);
    }, []);

    const onAkcionarFinishLogin = useCallback(
        (username: string, password: string, lang: string) => {
            setLoading(val => !val);
            loginApi(username, password, lang)
                .then(resp => {
                    setLevel('token', resp.token);
                    navigateWorkaround(routePathEnum.AKCIONAR_SMS);
                })
                .finally(() => setLoading(val => !val));
        },
        [navigateWorkaround, setLevel]
    );

    useEffect(() => {
        // check aktivacie uctu po aktivacii sa automaticky prihlasi
        if (loginDetails) {
            const timer = setInterval(
                onAkcionarFinishLogin,
                5000,
                loginDetails?.username,
                loginDetails?.password,
                language
            );
            return () => {
                clearInterval(timer);
            };
        }
    }, [language, loginDetails, onAkcionarFinishLogin]);

    const onFinish = (value: Store) => {
        setLoading(val => !val);
        registrace({
            captid: captchaState.id,
            email: value.email,
            jmeno: value.firstName,
            lang: language,
            password: value.password,
            prijmeni: value.lastName,
            telefon: value.phone,
            value: value.captcha,
            ...(tokenURL && {
                majetkovyucet: tokenURL
            }),
            ...(leiLogin?.leimigraceid && {
                leimigraceid: leiLogin?.leimigraceid
            })
        })
            .then(resp => {
                if (leiLogin?.leimigraceid) {
                    return loginApi(value.email, value.password, language)
                        .then(response => {
                            leiMigraceApi(leiLogin?.leimigraceid || '', response.token).then(() => {
                                setLevel('token', response.token);
                                setLevel('leiLogin', null);
                                navigateWorkaround(routePathEnum.MAIN_PAGE);
                            });
                        })
                        .finally(() => setLoading(val => !val));
                }
                if (resp.email === 'RUZNY' && tokenURL) {
                    setRenderError(true);
                    setLoginDetails({
                        username: value.email,
                        password: value.password
                    });
                }
                if (tokenURL) {
                    onAkcionarFinishLogin(value.email, value.password, language);
                }
                setSuccess(val => !val);
            })
            .catch((err: any) => {
                err.response?.data.message === 'Nevalidni captcha.'
                    ? setErrCaptcha('CHYBA_REGISTRACE_401')
                    : setErrCaptcha('CHYBA_REGISTRACE_404');

                callCaptcha(setLoadingCaptcha, setCaptchaState, setErrCaptcha);
            })
            .finally(() => setLoading(false));
    };
    if (renderError)
        return (
            <AuthWrapper>
                <Title text="AKCIONAR_PRIPOJENI" level={2} />
                <Paragraph text="AKCIONAR_PRIPOJENI_TXT" />
                <Title text="AKCIONAR_REGISTRACE_ERROR_TITLE" level={2} />
                <Paragraph text="AKCIONAR_REGISTRACE_ERROR_TEXT1" />
                <div
                    style={{
                        display: 'block',
                        paddingBottom: 12
                    }}
                />
                <Paragraph text="AKCIONAR_REGISTRACE_ERROR_TEXT2" />
            </AuthWrapper>
        );
    if (success)
        return (
            <AuthWrapper>
                <Title text="REGISTRACE" level={3} />
                <Paragraph text="EMP_REGISTRACE_OK" />
                <AnchorComponent link={true} to={routePathEnum.LOGIN_ROZCESTNIK}>
                    <ButtonComponent buttontext="ZPET" id="reigstraceZpetRedirectBtn" type="default" />
                </AnchorComponent>
            </AuthWrapper>
        );
    return (
        <AuthWrapper>
            {tokenURL && (
                <div
                    style={{
                        marginBottom: 20
                    }}
                >
                    <Title text="AKCIONAR_PRIPOJENI" level={2} />
                    <Paragraph text="AKCIONAR_PRIPOJENI_TXT" />
                </div>
            )}
            <FormWrapper
                onFinish={onFinish}
                onFinishFailed={(err: ValidateErrorEntity) => console.log('chyba:', err)}
                outForm={form}
            >
                <Title level={3} text={leiLogin?.mail ? 'LEI_PORTAL_TITLE' : 'NOVA_REGISTRACE_DEPOZITAR'} />
                {leiLogin?.mail && <Paragraph text={'LEI_REGISTRACE_TEXT'} />}
                <Title level={4} text="REGISTRACE_NADPIS" />
                <InputComponent
                    disabled={!!profily?.jmeno}
                    initialValue={profily?.jmeno}
                    label="JMENO"
                    name="firstName"
                />
                <InputComponent
                    disabled={!!profily?.prijmeni && !!profily?.jmeno}
                    initialValue={profily?.prijmeni}
                    label="PRIJMENI"
                    name="lastName"
                />
                <Title level={5} text="KONTAKTNI_UDAJE" dividerTitle={true} />
                <TranslateText>KONTAKTNI_UDAJE_EMAIL_INFO</TranslateText>
                <InputComponent
                    disabled={!!(profily || leiLogin?.mail)}
                    initialValue={profily?.email || leiLogin?.mail}
                    label="EMAIL"
                    name="email"
                    type="email"
                    rules={emailValidation()}
                />
                <InputComponent
                    disabled={!!profily}
                    initialValue={profily?.email2}
                    label="EMAIL_OVERENI"
                    name="emailCheck"
                    type="email"
                    dependencies={['email']}
                    rules={emailCheckValidation('email')}
                />
                <InputComponent
                    disabled={!!profily}
                    initialValue={profily?.telefon1}
                    label="TELEFON"
                    name="phone"
                    type="tel"
                    rules={phoneValidation()}
                    autoComplete="tel" // pred pridanim autocomplete sa tam z nejakeho dovodu objavoval mail
                />
                <Title level={5} text="LEI_AKTUALIZACE_PRIHLASOVACI_HESLO" dividerTitle={true} />
                <TranslateText>KONTAKTNI_UDAJE_HESLO_INFO</TranslateText>
                {PasswordInput()}
                <InputPassword
                    label="HESLO_OVERENI"
                    name="passwordCheck"
                    type="password"
                    dependencies={['password']}
                    rules={passwordCheckValidation('password')}
                />
                <LoadingWrapper loading={loadingCaptcha}>
                    <InputComponent
                        label="OPISTE_TEXT_Z_OBRAZKU"
                        name="captcha"
                        type="text"
                        idCaptcha={captchaState.id}
                        imageCaptcha={captchaState.image}
                    />
                </LoadingWrapper>
                <Row className="button-row">
                    <Col>
                        <AnchorComponent
                            link={true}
                            to={
                                prevPath.includes('lei')
                                    ? routePathEnum.LEI_LOGIN_MUJDEP
                                    : routePathEnum.LOGIN_ROZCESTNIK
                            }
                        >
                            <ButtonComponent
                                buttontext="ZPET"
                                id="reigstraceZpetBtn"
                                disabled={!!profily}
                                type="default"
                            />
                        </AnchorComponent>
                    </Col>
                    <Col>
                        <ButtonComponent
                            buttontext="REGISTROVAT"
                            id="reigstraceSubmitBtn"
                            onClick={() => form.submit()}
                            icon={<RightCircleFilled />}
                            loading={loading}
                            extramessage={errCaptcha}
                            disabled={!isStrongEnough}
                        />
                    </Col>
                </Row>
            </FormWrapper>
        </AuthWrapper>
    );
};

export default Registrace;
